'use client';

import React, { forwardRef, useEffect, useId } from 'react';
import { X } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { cn } from '~/lib/utils';

type ModalProps = {
  header?: React.ReactNode;
  subHeader?: React.ReactNode;
  /** Optional action row under the body (Cancel / Confirm, etc.). */
  footer?: React.ReactNode;
  /**
   * When false, body children are not mounted (lazy). Do not forward this to the
   * native `<dialog>` — open/close is imperative via `showModal()` / `close()`.
   */
  open?: boolean;
  /** Ref to the scrollable body — use it to reset scroll between steps/tabs. */
  bodyRef?: React.Ref<HTMLDivElement>;
  onClose?: () => void;
} & Omit<React.HTMLAttributes<HTMLDialogElement>, 'open'>;

/**
 * Modal facade — native `<dialog>` + `useModal` API.
 * Linear / Operate chrome: soft backdrop, rounded-xl panel, quiet close control.
 *
 * Spacing rhythm (DESIGN scale): header pt-5 → body pt-5 → footer mt-5/pt-4/pb-5.
 * Groups stay tight; section breaks use the same 20px beat.
 */
const Modal = forwardRef<HTMLDialogElement, ModalProps>(
  (
    { header: title, subHeader, footer, children, className, onClick, open, bodyRef, ...other },
    ref,
  ) => {
    const titleId = useId();
    const headerless = !title && !subHeader;
    // Lazy-mount body when callers pass open={isOpen}. Default (undefined) keeps body mounted.
    const showBody = open !== false;
    const hasBodyContent = showBody && children != null && children !== false;

    return (
      <dialog
        {...other}
        className={cn(
          'fixed inset-0 z-50 m-auto h-dvh w-dvw max-h-none max-w-none border-0 bg-transparent p-0',
          '[&::backdrop]:bg-black/40 [&::backdrop]:backdrop-blur-[1px]',
        )}
        aria-label={typeof title === 'string' ? title : 'Modal'}
        aria-labelledby={title ? titleId : undefined}
        ref={ref}
        onClick={(e) => {
          e.stopPropagation();
          if (e.target === e.currentTarget) {
            e.currentTarget.close();
          }
          onClick?.(e);
        }}
      >
        {/* Centering wrapper so portaled selects are not flex siblings of the panel. */}
        <div
          className="flex h-full w-full items-center justify-center p-6"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              (e.currentTarget.parentElement as HTMLDialogElement | null)?.close();
            }
          }}
        >
          <div
            style={{
              maxHeight: 'calc(100vh - 3em)',
            }}
            className={cn(
              'border-border/80 relative flex w-full min-w-0 max-w-md flex-col overflow-hidden rounded-xl border bg-background shadow-[0_16px_48px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.06)]',
              className,
            )}
            onClick={(e) => e.stopPropagation()}
          >
          {headerless ? (
            <form method="dialog" className="absolute right-2.5 top-2.5 z-10">
              <Button
                type="submit"
                variant="ghost"
                size="icon-sm"
                className="text-muted-foreground hover:text-foreground size-8 shrink-0"
                aria-label="Close"
              >
                <X className="size-4" />
              </Button>
            </form>
          ) : (
            <div className="bg-background shrink-0 pt-5">
              <div className="flex w-full items-center justify-between gap-3 px-6">
                {title ? (
                  <h3
                    id={titleId}
                    className="text-foreground min-w-0 flex-1 text-lg font-semibold tracking-tight"
                  >
                    {title}
                  </h3>
                ) : (
                  <div className="min-w-0 flex-1" />
                )}
                <form method="dialog" className="shrink-0">
                  <Button
                    type="submit"
                    variant="ghost"
                    size="icon-sm"
                    className="text-muted-foreground hover:text-foreground -mr-1.5 size-8 shrink-0"
                    aria-label="Close"
                  >
                    <X className="size-4" />
                  </Button>
                </form>
              </div>
              {subHeader && typeof subHeader === 'string' ? (
                <p className="text-muted-foreground mt-1 px-6 text-sm leading-5">{subHeader}</p>
              ) : null}
              {subHeader && typeof subHeader !== 'string' ? (
                <div className="text-muted-foreground mt-1 px-6 text-sm leading-5">{subHeader}</div>
              ) : null}
            </div>
          )}

          {hasBodyContent ? (
            <div
              ref={bodyRef}
              className={cn(
                'min-h-0 flex-1 overflow-y-auto px-6 pb-5',
                headerless ? 'pt-6' : 'pt-5',
              )}
            >
              {children}
            </div>
          ) : footer ? (
            // Preserve body rhythm when copy lives in the header (e.g. confirm dialogs).
            <div className="shrink-0 pt-5 pb-5" />
          ) : null}

          {footer ? (
            <div className="border-border/70 flex shrink-0 flex-wrap items-center justify-end gap-2 border-t px-6 pb-5 pt-4">
              {footer}
            </div>
          ) : null}
          </div>
        </div>
      </dialog>
    );
  },
);

type UseModalProps = {
  onClose?: () => void;
};

export const useModal = (props?: UseModalProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const modalRef = React.useRef<HTMLDialogElement>(null);
  const onClose = props?.onClose;

  const openModal = () => {
    modalRef.current?.showModal();
    setIsOpen(true);
  };

  const closeModal = () => {
    modalRef.current?.close();
    setIsOpen(false);
  };

  useEffect(() => {
    const el = modalRef.current;
    if (!el) return;

    const observer = new MutationObserver((records) => {
      const openUpdate = records.find((r) => r.attributeName === 'open');
      if (openUpdate) {
        const open = el.open;
        setIsOpen(open);

        if (!open && onClose) {
          onClose();
        }
      }
    });
    observer.observe(el, { attributes: true });
    return () => observer.disconnect();
  }, [onClose]);

  return { modalRef, openModal, closeModal, isOpen };
};

export default Modal;
